import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { characterList } from "@/mocks/characters";
import { newsList } from "@/mocks/news";
import useDocumentTitle from "@/hooks/useDocumentTitle";
import CharacterCard from "@/components/feature/CharacterCard";
import NewsCard from "@/components/feature/NewsCard";
import Breadcrumb from "@/components/base/Breadcrumb";
import EmptyState from "@/components/base/EmptyState";

export default function CharacterDetail() {
  const { id } = useParams();
  const character = characterList.find((c) => c.id === id);
  useDocumentTitle(
    character ? `${character.name}（${character.work}）角色介绍 - 漫蛙2` : "角色不存在 - 漫蛙2",
    character ? character.description.slice(0, 120) : "该角色资料不存在。"
  );

  const relatedCharacters = useMemo(() => {
    if (!character) return [];
    const byId = characterList.filter((c) => character.relatedCharacters.includes(c.id));
    const byWork = characterList.filter(
      (c) => c.id !== character.id && c.work === character.work && !character.relatedCharacters.includes(c.id)
    );
    const rest = characterList.filter(
      (c) => c.id !== character.id && !character.relatedCharacters.includes(c.id) && c.work !== character.work
    );
    return [...byId, ...byWork, ...rest].slice(0, 4);
  }, [character]);

  const relatedNews = useMemo(() => {
    if (!character) return [];
    const matched = newsList.filter((n) => character.relatedNews.includes(n.id));
    const rest = newsList.filter((n) => !character.relatedNews.includes(n.id));
    return [...matched, ...rest].slice(0, 3);
  }, [character]);

  const workLink = useMemo(
    () => (character ? `/updates/${character.workId}` : "/updates"),
    [character]
  );

  if (!character) {
    return (
      <div className="page-wrap py-16">
        <EmptyState
          icon="ri-user-unfollow-line"
          title="没有找到这位角色"
          description="角色资料可能不存在，返回角色列表看看其他角色吧。"
          actionLabel="返回角色列表"
          actionTo="/characters"
        />
      </div>
    );
  }

  return (
    <div className="pb-4">
      <div className="page-wrap py-6 md:py-8">
        <Breadcrumb items={[{ label: "角色介绍", to: "/characters" }, { label: character.name }]} />
      </div>

      <section className="page-wrap grid grid-cols-1 gap-8 lg:grid-cols-3">
        <div className="lg:col-span-1">
          <div className="overflow-hidden rounded-lg bg-background-200">
            <img
              src={character.avatar}
              alt={`${character.name} 角色立绘`}
              title={`${character.name} 角色介绍 - 漫蛙2`}
              className="aspect-square w-full object-cover object-top"
            />
          </div>
          <div className="mt-5 rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="text-xl font-extrabold text-foreground-950">{character.name}</h2>
            <p className="mt-1 text-sm text-foreground-500">{character.nameEn}</p>
            <div className="mt-4 flex flex-wrap gap-2">
              {character.tags.map((tag) => (
                <span key={tag} className="rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-800">
                  {tag}
                </span>
              ))}
            </div>
            <div className="mt-5 border-t border-background-200 pt-4">
              <span className="text-xs text-foreground-500">所属作品</span>
              <Link
                to={workLink}
                className="mt-1 flex items-center gap-1.5 text-sm font-semibold text-primary-600 transition-colors hover:text-primary-700"
              >
                <i className="ri-film-line"></i>
                {character.work}
                <i className="ri-arrow-right-line text-xs"></i>
              </Link>
            </div>
          </div>
        </div>

        <div className="lg:col-span-2">
          <h2 className="text-lg font-bold text-foreground-950">角色简介</h2>
          <p className="mt-3 text-[15px] leading-[1.9] text-foreground-800">{character.description}</p>

          <div className="mt-6 grid grid-cols-2 gap-4 rounded-lg border border-background-200 bg-background-50 p-5">
            <div>
              <span className="text-xs text-foreground-500">角色属性</span>
              <p className="mt-1 text-sm font-medium text-foreground-900">{character.tags.join(" · ")}</p>
            </div>
            <div>
              <span className="text-xs text-foreground-500">出场作品</span>
              <p className="mt-1 text-sm font-medium text-foreground-900">{character.work}</p>
            </div>
          </div>

          <section className="mt-8">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-foreground-950">该角色相关资讯</h2>
              <Link to="/news" className="text-sm font-medium text-primary-600 hover:text-primary-700">
                更多资讯
              </Link>
            </div>
            <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
              {relatedNews.map((item) => (
                <NewsCard key={item.id} item={item} />
              ))}
            </div>
          </section>

          <section className="mt-8">
            <h2 className="text-lg font-bold text-foreground-950">相关角色推荐</h2>
            <div className="mt-4 grid grid-cols-2 gap-4 md:grid-cols-4">
              {relatedCharacters.map((item) => (
                <CharacterCard key={item.id} item={item} />
              ))}
            </div>
          </section>
        </div>
      </section>
    </div>
  );
}